import { useState } from 'react';
import { toast } from 'sonner';
import { CheckIcon, CopyIcon } from 'lucide-react';
import { fetchInsightsSources } from '../../adminApi';
import { Panel } from '../../ui/Panel';
import { btnGhost } from '../../ui/styles';
import { SITE_URL } from '../../../data/seo';
import { qrImage } from '../../marketing/marketingRules';
import { sum } from '../../../core/rules/insights/stats';
import { ChartFrame } from '../charts/ChartFrame';
import { BarList } from '../charts/BarList';
import { ReportView } from '../ReportView';
import { useReport } from '../useReport';
import { fmt, ratio } from '../insightFormat';
import { channelLabel } from '../insightLabels';
import type { PeriodState } from '../../sales/usePeriodParams';
import type { OwnLink } from '../../../core/types/insights';
const linkUrl = (l: OwnLink) => `${SITE_URL}${l.path}?utm_source=${encodeURIComponent(l.utmSource)}`;

/**
 * Bronnen: hoe nieuwe klanten ons vonden (de vrijwillige vraag), wat ze opleveren, en eigen
 * links met QR-code voor de bio, flyers en kaartjes. Nooit in de nieuwsbrief (privacybelofte).
 */
export function SourcesTab({
  period
}: {
  period: PeriodState;
}) {
  const report = useReport(fetchInsightsSources, period.from, period.to);
  const span = `${period.from}-${period.to}`;
  return <ReportView report={report} what="De bronnen" data-id="element-2109" data-mp="2uzl7anmchc3b">
      {s => {
      const total = sum(s.rows.map(r => r.newCustomers));
      return <>
            <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)]" data-id="element-2110" data-mp="3smk6b50iew4d">
              <ChartFrame title="Hoe nieuwe klanten ons vonden" description="Antwoord op “Hoe kwam je bij ons?”, alleen bij de eerste keer" fileName={`bronnen-${span}`} table={{
            columns: [{
              label: 'Bron'
            }, {
              label: 'Nieuwe klanten'
            }],
            rows: s.rows.map(r => [channelLabel[r.channel], r.newCustomers])
          }} data-id="element-2111" data-mp="3npjvyhwmn77g">
                <BarList rows={s.rows.map(r => ({
              key: r.channel,
              label: <span className={r.channel === 'onbekend' ? 'text-muted' : ''} data-id="element-2113" data-mp="24q3vyflvkszn">{channelLabel[r.channel]}</span>,
              value: r.newCustomers,
              display: fmt(r.newCustomers),
              meta: ratio(r.newCustomers, total)
            }))} data-id="element-2112" data-mp="237cdxsltwzuy" />
              </ChartFrame>

              <ChartFrame title="Wat een bron oplevert" description="Omzet in deze periode, en gemiddeld per klant na 6 en 12 maanden (– als het nog te vroeg is)" tableOnly fileName={`bronnen-waarde-${span}`} table={{
            columns: [{
              label: 'Bron'
            }, {
              label: 'Nieuwe klanten'
            }, {
              label: 'Omzet',
              kind: 'money'
            }, {
              label: 'Na 6 maanden',
              kind: 'money'
            }, {
              label: 'Na 12 maanden',
              kind: 'money'
            }],
            rows: s.rows.map(r => [channelLabel[r.channel], r.newCustomers, r.revenueCents, r.value6mCents, r.value12mCents])
          }} data-id="element-2114" data-mp="1zsat9wjs6h44" />
            </div>

            <Panel title="Eigen links" description="Voor je Instagram-bio, een flyer of een kaartje. Je ziet ze terug bij Bereik › Bezoekers als bron." data-id="element-2115" data-mp="ofmb37fnzp1f">
              <ul className="grid gap-4 md:grid-cols-3" data-id="element-2116" data-mp="xegephrexfoh">
                {s.links.map(l => <OwnLinkCard key={l.id} link={l} data-id="element-2117" data-mp="seu1377o9xf5" />)}
              </ul>
              <p className="mt-4 text-[12px] leading-relaxed text-muted" data-id="element-2118" data-mp="17buiags8m9zd">
                De site haalt de bron uit de adresbalk zodra de pagina geladen is, zodat een doorgestuurde link niet ook als “flyer” telt. Nooit in de nieuwsbrief: daar beloven we geen meetlinks.
              </p>
            </Panel>
          </>;
    }}
    </ReportView>;
}
function OwnLinkCard({
  link
}: {
  link: OwnLink;
}) {
  const [copied, setCopied] = useState(false);
  const url = linkUrl(link);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error('Kopiëren lukte niet', {
        description: 'Selecteer de link en kopieer hem zelf.'
      });
    }
  };
  return <li className="flex gap-4 rounded-md bg-shell p-3" data-id="element-2119" data-mp="2dy76rcp5u7rd">
      <img src={qrImage(url, '#2b2622', '#ffffff')} alt={`QR-code voor ${link.label}`} className="h-20 w-20 shrink-0 rounded bg-white p-1" loading="lazy" data-id="element-2120" data-mp="2dyqs73e0rea5" />
      <div className="min-w-0 flex-1" data-id="element-2121" data-mp="1m7j8c8evfghe">
        <p className="text-[14px] text-ink" data-id="element-2122" data-mp="1m8lyge4ddew9">{link.label}</p>
        <p className="mt-0.5 break-all text-[12px] text-muted" data-id="element-2123" data-mp="1q833x7jsqc1f">{url.replace(/^https?:\/\//, '')}</p>
        <div className="mt-1.5 flex items-center justify-between gap-2" data-id="element-2124" data-mp="1v76ch1yzj0e5">
          <span className="text-[12px] text-muted" data-id="element-2125" data-mp="1va26if7pp4xb">{link.visits === null ? '– bezoeken' : `${fmt(link.visits)} bezoeken`}</span>
          <button type="button" onClick={() => void copy()} className={`${btnGhost} -mr-2`} data-id="element-2126" data-mp="36ne6gf0cp3e7">
            {copied ? <CheckIcon className="h-3.5 w-3.5" strokeWidth={2} aria-hidden="true" data-id="element-2127" data-mp="3bl69a42ja9x6" /> : <CopyIcon className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" data-id="element-2128" data-mp="zym682c91rg8" />}
            {copied ? 'Gekopieerd' : 'Kopieer'}
          </button>
        </div>
      </div>
    </li>;
}